const EventNames = require('@/lib/Events/EventNames');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const PaymentMethodRef = require('@/lib/PaymentMethods/PaymentMethodRef');
const Sentry = require('@sentry/browser');
const { container } = require('@/container');

/**
 * @param $location
 * @param $rootScope
 * @param $scope
 * @param $timeout
 * @param {AgentService} agentService
 * @param {ConnectivityChecker} connectivityChecker
 * @param {CurrentDevice} currentDevice
 * @param {DataStoreTester} dataStoreTester
 * @param {DeviceManager} deviceManager
 * @param {EInvoicingService} eInvoicingService
 * @param {LandingPageService} landingPageService
 * @param {LocalStorageDataStore} localStorageDataStore
 * @param {NavigationService} navigationService
 * @param {PaymentMethodService} paymentMethodService
 * @param {PrivilegesManager} privilegesManager
 * @param {SyncedDataManager} syncedDataManager
 * @param {SyncRunner} syncRunner
 * @param {TimeSyncer} timeSyncer
 * @param {ToastFactory} toastFactory
 * @param {TradingSessionManager} tradingSessionManager
 * @param {UserService} userService
 * @param {UserSessionManager} userSessionManager
 */
const EposIndexCtrl = function (
    $location,
    $rootScope,
    $scope,
    $timeout,
    agentService,
    connectivityChecker,
    currentDevice,
    dataStoreTester,
    deviceManager,
    eInvoicingService,
    landingPageService,
    localStorageDataStore,
    navigationService,
    paymentMethodService,
    privilegesManager,
    syncedDataManager,
    syncRunner,
    timeSyncer,
    toastFactory,
    tradingSessionManager,
    userService,
    userSessionManager
) {
    const logger = logFactory('EposIndexCtrl');

    navigationService.hideNav();
    $rootScope.rootProcessingMessage = null;

    /**
     * @type {Branch|null}
     */
    $scope.currentBranch = null;

    /**
     * @type {Device|null}
     */
    $scope.currentDevice = null;

    /**
     * @type {?User}
     */
    $scope.currentUser = null;

    $scope.redirectToLandingPage = function redirectToLandingPage() {
        // If a hash was present when the page was loaded, send the user to where they wanted to go.
        const requestedHash = window.requestedHash;

        // Clear the stored value so we don't go back there on the next login.
        window.requestedHash = undefined;

        const ignoreHashes = [
            '#/',
            '#/login',
            '#/device',
        ];
        if (
            requestedHash
            && ignoreHashes.indexOf(requestedHash) === -1
            && window.location.hash !== requestedHash
        ) {
            logger.log('Navigating to requested hash:', requestedHash);
            window.location.hash = requestedHash;

            return;
        }

        // Use the landing page code to determine which screen to show first.
        // It assumes "sell" if the code is either not set or is invalid.
        landingPageService.getTopLevelScreenPath(
            $scope.currentDevice.eposLandingPageKey,
            function (path) {
                logger.log('Navigating to landing page:', path);
                $timeout(() => $location.path(path));
            }
        );
    };

    $scope.init = async function init() {
        // If IndexedDB is not available log this to Sentry
        // When IndexedDB enters production we should additionally halt the application
        // and display a message as we do in the browser check
        try {
            const testResult = await dataStoreTester.test();
            logger.log('Testing IndexedDB support success.', `Test result: ${JSON.stringify(testResult)}`);
        } catch (e) {
            logger.error('IndexedDB not available.', e);

            // 73px is the total vertical offset of the other warning messages shown.
            document.body.innerHTML = `
                <div class="simple-browser-message" role="alert" style="padding-top: 73px;">
                    <div class="simple-browser-message__container">
                        <div class="alert alert-danger">
                            <h3>
                                <i class="fa-solid fa-exclamation-triangle"></i>
                                Error
                            </h3>
                            <p>ProPoint has detected a problem with its data storage. Please reset ProPoint to continue.</p>
                        </div>
                        <div>
                            <a class="btn btn-success"
                             href="/epos/reset.html">Continue</a>
                        </div>
                    </div>
                </div>
            `;

            return;
        }

        // Load a previously logged-in user from local storage and their user session.
        await userService.initialize();

        // If no user is logged in, redirect to the login page.
        if (!userService.isLoggedIn()) {
            logger.log('User not logged in. Redirecting to login page.');
            navigationService.viewLogin();
            return;
        }

        // We have a user. Ensure privileges are synced.
        await syncRunner.syncPrivileges();

        // Load a previously selected device from local storage.
        await currentDevice.initialize();

        // If no device is selected, redirect to the device selection page.
        if (!currentDevice.isSet()) {
            logger.log('Device not selected. Checking for guid saved on disk.');

            try {
                $timeout(() => {
                    $rootScope.setRootProcessingMessage('EPOS_INDEX.LOADING_DEVICE');
                });
                await deviceManager.loadCurrentDeviceFromGuidOnDisk();
            } catch (e) {
            }

            $timeout(() => {
                $rootScope.setRootProcessingMessage();
            });

            if (!currentDevice.isSet()) {
                logger.log('Device not selected. Redirecting to device page.');
                $timeout(() => {
                    navigationService.viewDevice();
                });
                return;
            }
        }

        // Now we have a logged-in user and a selected device.
        // Branches should also have been set by the device login listener.

        $scope.currentBranch = userService.currentBranch;
        $scope.currentDevice = currentDevice.device;
        $scope.currentUser = userService.currentUser;

        // Fire up a trading session for the device.
        try {
            $rootScope.setRootProcessingMessage('EPOS_INDEX.LOADING_TRADING_SESSION');

            const tradingSession = await tradingSessionManager.resumeOrOpenSession(
                $scope.currentDevice
            );
            currentDevice.setTradingSession(tradingSession);

            // Reset the privileges for the user.
            $rootScope.$broadcast(EventNames.TRADING_SESSION_CREATED);
        } catch (e) {
            if (!connectivityChecker.isOnline() && currentDevice.device.currentTradingSessionID) {
                // We're offline so can't check for a valid trading session.
                // But there is a trading session ID set on the device, so assume that it's still valid.
                console.warn(`Failed to validate device's trading session while offline. Assuming trading session ${currentDevice.device.currentTradingSessionID} is still valid.`);
            } else {
                // Send the user to the device selection screen.
                currentDevice.clearDevice();
                $scope.navigationService.viewDevice();
                return;
            }
        } finally {
            // Clear loading message.
            $rootScope.setRootProcessingMessage();
        }

        localStorageDataStore.clean();

        const syncStartedAt = new Date();
        syncRunner
            .syncEposStartupData(userService)
            .then(
                async function () {
                    logger.log(`Loading data finished in ${(new Date()) - syncStartedAt}ms`);

                    // If the current user does not appear in the privileges list they're probably a new user that
                    // did not exist when the last sync happened. Force a sync in this case.
                    // This would never cause 2 syncs to happen in a row because to get here without the current user
                    // in the privileges it must have been from the cache.
                    // This might not be able to happen anymore because we sync the privileges earlier on.
                    privilegesManager.findPrivilegesByUserID($scope.currentUser.ID)
                        .then(() => {
                            $timeout(() => {
                                $rootScope.appReady = true;
                                $scope.afterSync();
                            });
                        })
                        .catch(async () => {
                            logger.log('No privileges found for current user. Forcing re-sync.');

                            await syncedDataManager.clearData();

                            // We can only load data once per page load, so refresh the page.
                            window.location.reload();
                        });
                }
            );
    };

    $scope.afterSync = async () => {
        // Some tasks here can be performed in parallel.
        // Add the promises to this array and await them all instead of awaiting each one in turn for a faster startup.
        const promises = [];

        // Ensure we have the necessary custom fields for eInvoicing if required by the company.
        if (eInvoicingService.inUseForCompany()) {
            promises.push(eInvoicingService.initialize());
        }

        const paymentMethods = paymentMethodService.getAll();

        // Initialise Mellon WebECR payment method if applicable.
        const mellonPaymentMethod = paymentMethodService.getByRef(PaymentMethodRef.CARD_MELLON_WEBECR);
        if (mellonPaymentMethod) {
            // Use the container so we don't need to construct the driver if Mellon is not in use.
            promises.push(container.mellonWebECRDriver.initialize());
        }

        if (promises.length > 0) {
            await Promise.all(promises);
        }

        // Establish a connection to the ProPoint Agent if needed by this client.
        const agentRequiredReason = agentService.companyNeedsAgent(paymentMethods);
        if (agentRequiredReason) {
            agentService.connectToAgent(true, agentRequiredReason)
                .catch((error) => {
                    // The connectToAgent call shows an error to the user since we require Agent, but it's not
                    // available. Report it to Sentry as well so we know it happened.
                    Sentry.captureException(
                        new Error(
                            agentService.getRequiredConnectionFailedMessage(agentRequiredReason) + ' ' + error
                        )
                    );
                });
        }

        // Check the system timezone after a sync.
        if (userService.currentBranch) {
            timeSyncer.ensureComputerHasCorrectTimezoneForBranch(
                userService.currentBranch
            );
        }

        // See if there was a session in progress for the current user.
        // This would happen for user A if the switch user function was used to switch from user A to B, then
        // user A logs in again.
        userSessionManager.pullLastSessionScreen($scope.currentUser.ID)
            .then((previousScreen) => {
                $timeout(() => {
                    if (previousScreen) {
                        logger.log('Session restored. Redirecting to', previousScreen);
                        $location.path(previousScreen);
                    } else {
                        // Go to device's landing page when sync finished.
                        $scope.redirectToLandingPage();
                    }
                });
            });
    };

    $scope.init();
};

module.exports = EposIndexCtrl;
